import { createSlice } from "@reduxjs/toolkit";
import { API_STATUS } from "../../../constants/apiStatus";
import {
  getUsersAction,
  getUserByIdAction,
  createUserAction,
  updateUserAction,
  deleteUserAction,
} from "./usersSliceActions";
import { usersSliceInitialState } from "./usersSliceInitialState";

const initialState = usersSliceInitialState;

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
    resetUsersState: () => initialState,
  },
  extraReducers: (builder) => {
    // Get Users
    builder.addCase(getUsersAction.fulfilled, (state, action) => {
      state.apiStatus.getUsers = API_STATUS.COMPLETED;
      state.users = action.payload.users || [];
      // Backend no longer paginates. Normalize pagination object for backward compatibility.
      state.pagination = {
        enabled: false,
        currentPage: 1,
        totalPages: 1,
        totalUsers:
          action.payload.total ??
          (action.payload.users ? action.payload.users.length : 0),
        limit: action.payload.users ? action.payload.users.length : 0,
      };
      state.error = null;
    });
    builder.addCase(getUsersAction.pending, (state) => {
      state.apiStatus.getUsers = API_STATUS.STARTED;
      state.error = null;
    });
    builder.addCase(getUsersAction.rejected, (state, action) => {
      state.apiStatus.getUsers = API_STATUS.FAILED;
      state.error = action.payload;
    });

    // Get User By ID
    builder.addCase(getUserByIdAction.fulfilled, (state, action) => {
      state.apiStatus.getUserById = API_STATUS.COMPLETED;
      state.selectedUser = action.payload.user || action.payload;
      state.error = null;
    });
    builder.addCase(getUserByIdAction.pending, (state) => {
      state.apiStatus.getUserById = API_STATUS.STARTED;
      state.error = null;
    });
    builder.addCase(getUserByIdAction.rejected, (state, action) => {
      state.apiStatus.getUserById = API_STATUS.FAILED;
      state.error = action.payload;
    });

    // Create User
    builder.addCase(createUserAction.fulfilled, (state, action) => {
      state.apiStatus.createUser = API_STATUS.COMPLETED;
      state.users.unshift(action.payload);
      state.error = null;
    });
    builder.addCase(createUserAction.pending, (state) => {
      state.apiStatus.createUser = API_STATUS.STARTED;
      state.error = null;
    });
    builder.addCase(createUserAction.rejected, (state, action) => {
      state.apiStatus.createUser = API_STATUS.FAILED;
      state.error = action.payload;
    });

    // Update User
    builder.addCase(updateUserAction.fulfilled, (state, action) => {
      state.apiStatus.updateUser = API_STATUS.COMPLETED;
      const index = state.users.findIndex(
        (user) => user.id === action.payload.id
      );
      if (index !== -1) {
        state.users[index] = action.payload;
      }
      if (state.selectedUser && state.selectedUser.id === action.payload.id) {
        state.selectedUser = action.payload;
      }
      state.error = null;
    });
    builder.addCase(updateUserAction.pending, (state) => {
      state.apiStatus.updateUser = API_STATUS.STARTED;
      state.error = null;
    });
    builder.addCase(updateUserAction.rejected, (state, action) => {
      state.apiStatus.updateUser = API_STATUS.FAILED;
      state.error = action.payload;
    });

    // Delete User
    builder.addCase(deleteUserAction.fulfilled, (state, action) => {
      state.apiStatus.deleteUser = API_STATUS.COMPLETED;
      state.users = state.users.filter((user) => user.id !== action.payload);
      if (state.selectedUser && state.selectedUser.id === action.payload) {
        state.selectedUser = null;
      }
      state.error = null;
    });
    builder.addCase(deleteUserAction.pending, (state) => {
      state.apiStatus.deleteUser = API_STATUS.STARTED;
      state.error = null;
    });
    builder.addCase(deleteUserAction.rejected, (state, action) => {
      state.apiStatus.deleteUser = API_STATUS.FAILED;
      state.error = action.payload;
    });
  },
});

export const {
  clearError,
  setSelectedUser,
  clearSelectedUser,
  resetUsersState,
} = usersSlice.actions;

export default usersSlice.reducer;
